/**
 * useNoteSigning Hook
 *
 * Manages note signing workflow including:
 * - Sign note (with electronic signature when the org enables note signing)
 * - Complete note (without signature when the org disables note signing)
 * - Amend completed note
 * - Signing onboarding flow
 *
 * Handles all signing-related mutations, analytics, and user feedback.
 */

import { useCallback, useState } from 'react';
import { notesApi, useUpdateNoteMutation } from '@features/notes';
import { useUser } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { useSnackbar } from 'notistack';

/**
 * True when a mutation error is the backend's 409 "note is already co-signed"
 * guard — i.e. this tab's view of the note is stale (e.g. the supervisor
 * co-signed after this tab last loaded it).
 */
const isAlreadyCosignedConflict = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409;

/**
 * Extract the backend's message from a 403 mutation error — e.g. the
 * author-only reopen guard ("Only the note's author can reopen this note...").
 * Returns null for any other error shape.
 */
const getForbiddenMessage = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const { status, data } = error as { status?: unknown; data?: { message?: unknown } };
  if (status !== 403) return null;
  return typeof data?.message === 'string' && data.message ? data.message : null;
};

interface UseNoteSigningOptions {
  noteId: string;
  encounterId?: string;
  hasPatient?: boolean;
  hasNoteSigning?: boolean;
  isSigned?: boolean;
  needsCosign?: boolean;
  onNoteFinalized?: () => void | Promise<void>;
}

interface UseNoteSigningReturn {
  /** Whether the signing onboarding dialog is open */
  isOnboardingOpen: boolean;
  /** Set onboarding dialog open state */
  setIsOnboardingOpen: (open: boolean) => void;
  /** Whether a note update is in progress */
  isUpdatingNote: boolean;
  /** Handle Sign Note button click - signs with signature or completes without */
  handleSignNote: () => Promise<void>;
  /** Handle Amend Note from completed mode */
  handleAmendNote: () => Promise<void>;
  /** Handle onboarding completion - signs note after onboarding */
  handleOnboardingComplete: () => Promise<void>;
  /** Handle resubmit for co-sign (from edits_requested → ready) */
  handleResubmit: () => Promise<void>;
}

export function useNoteSigning({
  noteId,
  encounterId,
  hasPatient = false,
  hasNoteSigning = false,
  isSigned = false,
  needsCosign = false,
  onNoteFinalized,
}: UseNoteSigningOptions): UseNoteSigningReturn {
  const { enqueueSnackbar } = useSnackbar();
  const { note: noteMetrics } = useEventTracking();
  const { userState } = useUser();
  const dispatch = useAppDispatch();

  // Local state for note signing onboarding dialog
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  /**
   * Handle the backend's 409 "already co-signed" guard: this tab was stale.
   * Refetch the note so the UI shows the true signed state (and the sign
   * affordances disappear). Returns true when the error was handled.
   */
  const handleAlreadyCosigned = useCallback(
    (error: unknown): boolean => {
      if (!isAlreadyCosignedConflict(error)) return false;
      enqueueSnackbar(
        'This note has already been co-signed — refreshing to the latest version.',
        { variant: 'info' },
      );
      dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
      return true;
    },
    [dispatch, enqueueSnackbar, noteId],
  );

  // RTK Query mutation
  const [updateNote, { isLoading: isUpdatingNote }] = useUpdateNoteMutation();

  const notifyNoteFinalized = useCallback(() => {
    if (!onNoteFinalized) return;
    void Promise.resolve(onNoteFinalized()).catch(error => {
      console.error('Failed to run note finalization callback:', error);
    });
  }, [onNoteFinalized]);

  /**
   * Perform note signing operation (with electronic signature)
   * Used when the org enables note signing and the note has a patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteSign = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      notifyNoteFinalized();

      // Show success message
      enqueueSnackbar(
        needsCosign ? 'Note submitted for co-signature' : 'Note signed successfully!',
        { variant: 'success' },
      );

      // Track analytics
      noteMetrics.sign(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
      });
    } catch (error) {
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to sign note:', error);
      enqueueSnackbar('Failed to sign note. Please try again.', { variant: 'error' });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'sign',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, noteMetrics, hasPatient, encounterId, needsCosign, handleAlreadyCosigned]);

  /**
   * Perform note completion (without electronic signature)
   * Used when the org disables note signing or the note has no patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteComplete = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      notifyNoteFinalized();

      if (needsCosign) {
        enqueueSnackbar('Note submitted for co-signature', { variant: 'success' });
      }

      // Note completed successfully - no additional tracking needed
      // (completion without signing is a valid outcome)
    } catch (error) {
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to complete note:', error);
      enqueueSnackbar('Failed to complete note. Please try again.', { variant: 'error' });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, noteMetrics, encounterId, needsCosign, handleAlreadyCosigned]);

  /**
   * Handle Sign Note button click
   * - When org note signing is on + patient: signs with electronic signature (shows onboarding if needed)
   * - For other users: completes without signature
   */
  const handleSignNote = useCallback(async () => {
    if (!noteId) return;

    // Track sign/complete click
    noteMetrics.reviewSignClicked?.(noteId, {
      has_patient: hasPatient,
      has_note_signing: hasNoteSigning,
      encounter_id: encounterId,
    });

    if (hasNoteSigning && hasPatient) {
      // Users with signing capability - check if onboarding is needed first
      if (!userState?.onboardingSteps?.note_signing_onboarding_completed) {
        setIsOnboardingOpen(true);
        return; // Show onboarding instead of signing
      }

      // Perform the signing operation
      await performNoteSign();
    } else {
      // Users without signing capability - complete directly
      // Track Complete click
      noteMetrics.completeClicked?.(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
        encounter_id: encounterId,
      });

      await performNoteComplete();
    }
  }, [
    noteId,
    hasPatient,
    hasNoteSigning,
    encounterId,
    noteMetrics,
    userState,
    performNoteSign,
    performNoteComplete,
  ]);

  /**
   * Handle Amend Note from completed mode
   * Reopens a completed note for editing
   */
  const handleAmendNote = useCallback(async () => {
    if (!noteId) return;

    // Track Amend click
    noteMetrics.amendClicked?.(noteId, {
      was_signed: isSigned,
      encounter_id: encounterId,
    });

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'reviewing',
        },
      }).unwrap();

      // Note amended successfully - status changed back to reviewing
    } catch (error) {
      console.error('Failed to amend note:', error);
      const forbiddenMessage = getForbiddenMessage(error);
      enqueueSnackbar(forbiddenMessage ?? 'Failed to reopen note. Please try again.', {
        variant: 'error',
      });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'amend',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, enqueueSnackbar, noteMetrics, isSigned, encounterId]);

  /**
   * Handle onboarding completion
   * Called when user finishes the signing onboarding flow
   * Automatically signs the note after onboarding completes
   */
  const handleOnboardingComplete = useCallback(async () => {
    // Close the onboarding dialog
    setIsOnboardingOpen(false);

    // Perform the signing operation
    await performNoteSign();
  }, [performNoteSign]);

  /**
   * Handle resubmit for co-sign (from edits_requested → completed)
   * Used when author has addressed supervisor feedback and is resubmitting.
   * Backend routes to "ready" internally based on requires_cosigning.
   */
  const handleResubmit = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      enqueueSnackbar('Note resubmitted for co-signature', { variant: 'success' });
    } catch (error) {
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to resubmit note:', error);
      enqueueSnackbar('Failed to resubmit note. Please try again.', { variant: 'error' });

      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, enqueueSnackbar, noteMetrics, encounterId, handleAlreadyCosigned]);

  return {
    isOnboardingOpen,
    setIsOnboardingOpen,
    isUpdatingNote,
    handleSignNote,
    handleAmendNote,
    handleOnboardingComplete,
    handleResubmit,
  };
}
